import React, { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { PanelLeftClose, PanelLeftOpen, LogOut, ChevronRight } from 'lucide-react';
import { Menu, MenuButton, MenuItem } from '@szhsin/react-menu';
import { NavData, SecondaryNavData } from '../Constant';
import SessionsListSection from './SessionsListSection';
import BrandLockup from '../BrandLockup';
import { useContextHook } from 'use-context-hook';
import { AuthContext } from '../../Context/authContext';
import agentService from '../../services/agentService';
import { useQuery } from '@tanstack/react-query';
import { cn } from '../../lib/utils';

const FERDY_ID = '6d1cd244-2e8e-4782-981d-24cae676f7c7';

const SideBar = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [agentData, setAgentData] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState({
    label: 'All',
    value: '',
  });

  const navigate = useNavigate();
  const location = useLocation();

  const { user, onLogout } = useContextHook(AuthContext, v => ({
    user: v.user,
    onLogout: v.onLogout,
  }));

  const getAllAgentData = useQuery({
    queryKey: ['get-all-agents'],
    queryFn: () => agentService.getAllAgent(),
    staleTime: Infinity,
    gcTime: Infinity,
  });

  useEffect(() => {
    if (getAllAgentData.isSuccess) {
      setAgentData([
        { agent_name: 'All', id: null },
        { agent_name: 'Ferdy', id: FERDY_ID },
        ...(getAllAgentData?.data?.agents || []),
      ]);
    }
  }, [getAllAgentData.isSuccess, getAllAgentData?.data?.agents]);

  useEffect(() => {
    if (!collapsed) {
      document.body.classList.add('sidebar-active');
    } else {
      document.body.classList.remove('sidebar-active');
    }
  }, [collapsed]);

  const isActive = item => {
    const path = `/${item.path}`;
    if (item.exact) return location.pathname === path;
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const initial = user?.first_name?.trim()?.[0]?.toUpperCase() || 'U';
  const displayName = user?.first_name || 'User';

  const linkClass = (active, justifyCenter) =>
    cn(
      'flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors relative',
      justifyCenter && 'justify-center',
      active
        ? 'bg-white text-ink font-semibold shadow-soft'
        : 'text-ink/80 hover:text-ink hover:bg-white/80',
    );

  return (
    <aside
      className={cn(
        'flex-shrink-0 bg-paper-2 border-r border-shell-border flex flex-col h-screen sticky top-0 transition-all duration-200 z-50',
        collapsed ? 'w-16' : 'w-64',
      )}>
      <div
        className={cn(
          'h-16 flex items-center border-b border-shell-border flex-shrink-0',
          collapsed ? 'px-3 justify-center gap-1' : 'px-4 justify-between',
        )}>
        <BrandLockup collapsed={collapsed} />
        <button
          type="button"
          onClick={() => setCollapsed(c => !c)}
          className="p-1.5 rounded-md text-ink/60 hover:text-ink hover:bg-white/70"
          title={collapsed ? 'Expand' : 'Collapse'}>
          {collapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
        </button>
      </div>

      <nav className="flex-1 min-h-0 flex flex-col overflow-hidden py-3">
        <ul className="space-y-0.5 px-2 flex-shrink-0">
          {NavData.map(item => {
            const active = isActive(item);
            const Icon = item.icon;
            return (
              <li key={item.path}>
                <NavLink
                  to={`/${item.path}`}
                  title={collapsed ? item.name : undefined}
                  className={linkClass(active, collapsed)}>
                  {active ? (
                    <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-primary rounded-r" />
                  ) : null}
                  <Icon className={cn('w-4 h-4 flex-shrink-0', active ? 'text-ink' : 'text-ink/70')} />
                  {!collapsed && <span className="truncate">{item.name}</span>}
                </NavLink>
              </li>
            );
          })}
        </ul>

        {!collapsed && (
          <div className="mt-6 px-2 flex-1 min-h-0 flex flex-col">
            <div className="flex items-center justify-between mb-2 px-2 flex-shrink-0">
              <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-ink/55">· Chats</span>
              <Menu
                menuButton={
                  <MenuButton className="text-xs text-ink/70 hover:text-ink bg-transparent border-0 cursor-pointer p-0 font-medium">
                    {selectedAgent?.label || 'All'}
                  </MenuButton>
                }>
                {agentData?.map((data, index) => (
                  <MenuItem
                    key={index}
                    onClick={() => setSelectedAgent({ label: data?.agent_name, value: data?.id })}>
                    {data?.agent_name}
                  </MenuItem>
                ))}
              </Menu>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-1">
              <SessionsListSection selectedAgent={selectedAgent} />
            </div>
          </div>
        )}
      </nav>

      <div className="flex-shrink-0 border-t border-shell-border px-2 py-2">
        <ul className="space-y-0.5">
          {SecondaryNavData.map(item => {
            if (item.adminOnly && user?.plan?.type !== 'admin') return null;
            const active = isActive(item);
            const Icon = item.icon;
            return (
              <li key={item.path}>
                <NavLink
                  to={`/${item.path}`}
                  title={collapsed ? item.name : undefined}
                  className={linkClass(active, collapsed)}>
                  <Icon className={cn('w-4 h-4 flex-shrink-0', active ? 'text-ink' : 'text-ink/70')} />
                  {!collapsed && item.name}
                </NavLink>
              </li>
            );
          })}
          <li>
            <button
              type="button"
              onClick={onLogout}
              title={collapsed ? 'Logout' : undefined}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-ink/80 hover:text-ink hover:bg-white/80',
                collapsed && 'justify-center',
              )}>
              <LogOut className="w-4 h-4 flex-shrink-0 text-ink/70" />
              {!collapsed && 'Logout'}
            </button>
          </li>
        </ul>
      </div>

      <div
        className="border-t border-shell-border p-3 flex-shrink-0"
        onClick={() => navigate('/settings')}
        onKeyDown={e => e.key === 'Enter' && navigate('/settings')}
        role="button"
        tabIndex={0}
        title="Settings">
        <div
          className={cn(
            'flex items-center gap-3 px-2 py-2 rounded-md hover:bg-white/80 cursor-pointer',
            location.pathname === '/settings' && 'bg-white',
            collapsed && 'justify-center',
          )}>
          <div className="w-8 h-8 rounded-full bg-ink flex items-center justify-center text-white text-sm font-extrabold flex-shrink-0">
            {initial}
          </div>
          {!collapsed && (
            <>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-ink truncate">{displayName}</p>
                <p className="text-xs text-ink/55 truncate">Free plan</p>
              </div>
              <ChevronRight className="w-4 h-4 text-ink/50" />
            </>
          )}
        </div>
      </div>
    </aside>
  );
};

export default SideBar;
